import { twMerge } from 'tailwind-merge';

import errorPage from '~/assets/error-page.png';
import { paths } from '~/constants/paths';
import { SUPPORT_EMAIL, SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY } from '~/utils/globals';

export type ErrorType =
  | 'Not_Authorized'
  | 'Not_Found'
  | 'Maintenance'
  | 'Pending'
  | 'Onboarding'
  | 'Unexpected';

type ErrorPageProps = {
  className?: string;
  type: ErrorType;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonHref?: string;
};

type ComponentProps = {
  title: string;
  subtitle?: string;
  buttonText?: string;
  buttonHref?: string;
};

type ComponentConfig = {
  [key in ErrorType]: ComponentProps;
};

export function ErrorPage({
  className,
  type,
  title,
  subtitle,
  buttonText,
  buttonHref,
}: ErrorPageProps) {
  const componentConfig: ComponentConfig = {
    Not_Authorized: {
      title: '401 - You do not have permission to do that.',
      buttonText: 'Go back',
      buttonHref: paths.app,
    },
    Not_Found: {
      title: '404 - Oops! The page you requested does not seem to be working.',
      buttonText: 'Go back',
      buttonHref: paths.app,
    },
    Maintenance: {
      title: 'Hey there! We’re working on things — stay tuned for more!',
      subtitle: 'If you have any immediate concerns, please reach out to us.',
    },
    Pending: {
      title: 'Hey there! Your request to join your organization is pending.',
      subtitle: 'Check back soon, or reach out to us to get started!',
    },
    Onboarding: {
      title:
        'Your supplier account is being verified! Please contact your Account Rep for more information.',
    },
    Unexpected: {
      title: 'Something went wrong.',
      subtitle: 'Please try again or contact support if the issue persists.',
      buttonText: 'Go back',
      buttonHref: paths.app,
    },
  };
  const config = componentConfig[type];
  const resolvedButtonText = buttonText ?? config.buttonText;
  const resolvedButtonHref = buttonHref ?? config.buttonHref;

  return (
    <div className={twMerge('mx-auto max-w-7xl px-8 pt-0', className)}>
      <section className="my-4 flex flex-col sm:my-12 sm:flex-row sm:px-0">
        <div className="mr-4 sm:mr-10">
          <h1 className="text-4xl font-extrabold tracking-tighter text-gray-900 sm:text-6xl">
            {title ?? config.title}
          </h1>
          <p className="mt-6 mb-7 text-sm text-gray-500 sm:text-xl">
            {subtitle ?? config.subtitle}
          </p>
          {resolvedButtonText && resolvedButtonHref ? (
            <a
              href={resolvedButtonHref}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-900 px-5 py-3 text-sm font-medium text-white"
            >
              {resolvedButtonText}
            </a>
          ) : (
            ''
          )}
          <p className="mt-8 text-sm text-gray-500">
            Need help? Contact us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-teal-900 underline">
              {SUPPORT_EMAIL}
            </a>{' '}
            or{' '}
            <a href={`tel:${SUPPORT_PHONE}`} className="font-medium text-teal-900 underline">
              {SUPPORT_PHONE_DISPLAY}
            </a>
            .
          </p>
        </div>
        <img src={errorPage} alt="4 People" className="mt-11 w-fit sm:mt-0" />
      </section>
    </div>
  );
}
